/**
 * Invoice-level adjustments (finance request 2026-09-10, items 2 and 6).
 *
 * Two knobs the finance team asked for on the FINAL invoice:
 *   - a discount, percent based or a fixed dollar amount, applied BEFORE tax;
 *   - a late fee, percent based or fixed, applied AFTER tax and after the
 *     deposit already paid (the percent is charged on what is still owed).
 *
 * This module is pure arithmetic and part of the shared engine (`appCalcutions`),
 * so the web app, the pdf-service and the backend bundle all run the very same
 * code. `getInvoiceRows` calls `applyInvoiceAdjustments` with the figures it
 * already computes; nothing here reads shifts, rates or terms.
 *
 * Money rules (decision Jhony 2026-09-10, "discount before tax"):
 *   subtotal              = everything before tax (taxable labor + travel + non-taxable charges)
 *   discountAmount        = percent × subtotal | min(fixed, subtotal)
 *   taxableAfterDiscount  = taxableBase × (1 − discountAmount / subtotal)   ← pro-rata share
 *   taxAmount             = taxRate × taxableAfterDiscount                 ← only recomputed when a discount exists
 *   total                 = subtotal − discountAmount + taxAmount
 *   balance               = total − deposit
 *   lateFeeAmount         = percent × max(balance, 0) | fixed              ← never taxed
 *   amountDue             = balance + lateFeeAmount
 * Every figure is rounded to cents as it is produced (same `round2` the server uses).
 */

export type InvoiceAdjustmentType = "percent" | "fixed";

export interface InvoiceAdjustmentInput {
  type: InvoiceAdjustmentType;
  value: number;
}

export interface InvoiceAdjustmentsInput {
  discount?: InvoiceAdjustmentInput | null;
  lateFee?: InvoiceAdjustmentInput | null;
}

export interface InvoiceAdjustmentLine extends InvoiceAdjustmentInput {
  /** Dollar amount the line represents on this invoice (rounded to cents). */
  amount: number;
  /** "Discount (10%)" / "Late Fee ($25.00)" — what the PDF and the screens print. */
  label: string;
}

export interface InvoiceAdjustmentsCalc {
  hasDiscount: boolean;
  hasLateFee: boolean;
  discount: InvoiceAdjustmentLine | null;
  lateFee: InvoiceAdjustmentLine | null;
  /** Pre-tax subtotal before the discount. */
  subtotal: number;
  /** Taxable part of `subtotal` before the discount. */
  taxableBase: number;
  discountAmount: number;
  taxableAfterDiscount: number;
  taxRate: number;
  /** Tax the engine computed on the undiscounted base. */
  taxAmountBeforeDiscount: number;
  /** Tax actually charged (recomputed only when there is a discount). */
  taxAmount: number;
  /** subtotal − discount + tax. */
  total: number;
  deposit: number;
  /** total − deposit (can be negative when the deposit exceeds the total). */
  balance: number;
  lateFeeAmount: number;
  /** balance + late fee — what the client owes. */
  amountDue: number;
}

export const INVOICE_ADJUSTMENT_LIMITS = Object.freeze({
  maxPercent: 100,
  maxFixed: 1_000_000,
});

/** Validation messages shared by the web form and the API (400 body). */
export const INVOICE_ADJUSTMENT_ERRORS = Object.freeze({
  discountType: "Discount type must be percent or fixed",
  discountValue: "Discount must be a number greater than or equal to 0",
  discountPercent: `Discount percent must be between 0 and ${INVOICE_ADJUSTMENT_LIMITS.maxPercent}`,
  discountFixed: `Discount cannot exceed $${INVOICE_ADJUSTMENT_LIMITS.maxFixed.toLocaleString("en-US")}`,
  lateFeeType: "Late fee type must be percent or fixed",
  lateFeeValue: "Late fee must be a number greater than or equal to 0",
  lateFeePercent: `Late fee percent must be between 0 and ${INVOICE_ADJUSTMENT_LIMITS.maxPercent}`,
  lateFeeFixed: `Late fee cannot exceed $${INVOICE_ADJUSTMENT_LIMITS.maxFixed.toLocaleString("en-US")}`,
});

export function roundMoney(value: unknown): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

const toFiniteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

const normalizeType = (value: unknown): InvoiceAdjustmentType | null => {
  const t = String(value ?? "")
    .trim()
    .toLowerCase();
  if (t === "percent" || t === "percentage" || t === "%") return "percent";
  if (t === "fixed" || t === "amount" || t === "dollar" || t === "$") return "fixed";
  return null;
};

interface NormalizeLineResult {
  line: InvoiceAdjustmentInput | null;
  errors: string[];
}

const normalizeLine = (
  raw: unknown,
  messages: { type: string; value: string; percent: string; fixed: string },
): NormalizeLineResult => {
  // Absent / null / empty object → no adjustment, no error.
  if (raw === null || raw === undefined) return { line: null, errors: [] };
  if (typeof raw !== "object") return { line: null, errors: [messages.value] };
  const obj = raw as { type?: unknown; value?: unknown };
  const value = toFiniteNumber(obj.value);
  const hasValue = obj.value !== undefined && obj.value !== null && String(obj.value).trim() !== "";
  if (!hasValue) return { line: null, errors: [] };
  const type = normalizeType(obj.type);
  const errors: string[] = [];
  if (!type) errors.push(messages.type);
  if (value === null || value < 0) errors.push(messages.value);
  if (type === "percent" && value !== null && value > INVOICE_ADJUSTMENT_LIMITS.maxPercent) {
    errors.push(messages.percent);
  }
  if (type === "fixed" && value !== null && value > INVOICE_ADJUSTMENT_LIMITS.maxFixed) {
    errors.push(messages.fixed);
  }
  if (errors.length) return { line: null, errors };
  // A zero value means "no adjustment" — keeps the document clean.
  if (!value || !type) return { line: null, errors: [] };
  return { line: { type, value: roundMoney(value) }, errors: [] };
};

/**
 * Turns whatever the client posted into a clean `{ discount, lateFee }` (or
 * `null` when nothing is set). Never throws: the caller decides what to do with
 * `errors` (the API answers 400 with them; the form shows them inline).
 */
export function normalizeInvoiceAdjustments(raw: unknown): {
  adjustments: InvoiceAdjustmentsInput | null;
  errors: string[];
} {
  if (raw === null || raw === undefined) return { adjustments: null, errors: [] };
  if (typeof raw !== "object") {
    return { adjustments: null, errors: ["Invoice adjustments must be an object"] };
  }
  const obj = raw as { discount?: unknown; lateFee?: unknown };
  const discount = normalizeLine(obj.discount, {
    type: INVOICE_ADJUSTMENT_ERRORS.discountType,
    value: INVOICE_ADJUSTMENT_ERRORS.discountValue,
    percent: INVOICE_ADJUSTMENT_ERRORS.discountPercent,
    fixed: INVOICE_ADJUSTMENT_ERRORS.discountFixed,
  });
  const lateFee = normalizeLine(obj.lateFee, {
    type: INVOICE_ADJUSTMENT_ERRORS.lateFeeType,
    value: INVOICE_ADJUSTMENT_ERRORS.lateFeeValue,
    percent: INVOICE_ADJUSTMENT_ERRORS.lateFeePercent,
    fixed: INVOICE_ADJUSTMENT_ERRORS.lateFeeFixed,
  });
  const errors = [...discount.errors, ...lateFee.errors];
  if (errors.length) return { adjustments: null, errors };
  if (!discount.line && !lateFee.line) return { adjustments: null, errors: [] };
  return {
    adjustments: {
      discount: discount.line,
      lateFee: lateFee.line,
    },
    errors: [],
  };
}

export function hasInvoiceAdjustments(input: InvoiceAdjustmentsInput | null | undefined): boolean {
  return Boolean(input?.discount || input?.lateFee);
}

export function formatAdjustmentLabel(
  kind: "Discount" | "Late Fee",
  line: InvoiceAdjustmentInput | null | undefined,
): string {
  if (!line) return kind;
  if (line.type === "percent") {
    const pct = Number.isInteger(line.value) ? String(line.value) : String(roundMoney(line.value));
    return `${kind} (${pct}%)`;
  }
  return kind;
}

export function applyInvoiceAdjustments(args: {
  subtotal: unknown;
  taxableBase: unknown;
  taxRate: unknown;
  /** Tax the engine already computed on the undiscounted base. */
  taxAmount: unknown;
  deposit?: unknown;
  adjustments?: InvoiceAdjustmentsInput | null;
}): InvoiceAdjustmentsCalc {
  const subtotal = roundMoney(args.subtotal);
  const taxableBase = roundMoney(args.taxableBase);
  const taxRate = Number(args.taxRate);
  const safeTaxRate = Number.isFinite(taxRate) && taxRate > 0 ? taxRate : 0;
  const taxAmountBeforeDiscount = roundMoney(args.taxAmount);
  const deposit = roundMoney(args.deposit);
  const discountInput = args.adjustments?.discount ?? null;
  const lateFeeInput = args.adjustments?.lateFee ?? null;

  let discountAmount = 0;
  if (discountInput && discountInput.value > 0 && subtotal > 0) {
    discountAmount =
      discountInput.type === "percent"
        ? roundMoney((subtotal * discountInput.value) / 100)
        : roundMoney(Math.min(discountInput.value, subtotal));
  }
  const hasDiscount = discountAmount > 0;
  const ratio = hasDiscount && subtotal > 0 ? discountAmount / subtotal : 0;
  const taxableAfterDiscount = hasDiscount ? roundMoney(taxableBase * (1 - ratio)) : taxableBase;
  const taxAmount = hasDiscount
    ? roundMoney((taxableAfterDiscount * safeTaxRate) / 100)
    : taxAmountBeforeDiscount;
  const total = roundMoney(subtotal - discountAmount + taxAmount);
  const balance = roundMoney(total - deposit);

  let lateFeeAmount = 0;
  if (lateFeeInput && lateFeeInput.value > 0) {
    lateFeeAmount =
      lateFeeInput.type === "percent"
        ? roundMoney((Math.max(balance, 0) * lateFeeInput.value) / 100)
        : roundMoney(lateFeeInput.value);
  }
  const hasLateFee = lateFeeAmount > 0;
  const amountDue = roundMoney(balance + lateFeeAmount);

  return {
    hasDiscount,
    hasLateFee,
    discount: hasDiscount
      ? {
          type: discountInput!.type,
          value: discountInput!.value,
          amount: discountAmount,
          label: formatAdjustmentLabel("Discount", discountInput),
        }
      : null,
    lateFee: hasLateFee
      ? {
          type: lateFeeInput!.type,
          value: lateFeeInput!.value,
          amount: lateFeeAmount,
          label: formatAdjustmentLabel("Late Fee", lateFeeInput),
        }
      : null,
    subtotal,
    taxableBase,
    discountAmount,
    taxableAfterDiscount,
    taxRate: safeTaxRate,
    taxAmountBeforeDiscount,
    taxAmount,
    total,
    deposit,
    balance,
    lateFeeAmount,
    amountDue,
  };
}
